import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ShieldAlert, Unlock, Search, Phone, History, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blacklist = () => {
  const [blacklist, setBlacklist] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [unlockPhone, setUnlockPhone] = useState(null);

  useEffect(() => {
    loadBlacklist();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      setFilteredList(
        blacklist.filter(item => item.telefono.includes(searchTerm))
      );
    } else {
      setFilteredList(blacklist);
    }
  }, [blacklist, searchTerm]);

  const loadBlacklist = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/blacklist`);
      setBlacklist(res.data);
    } catch (error) {
      console.error('Error fetching blacklist:', error);
      toast.error('Error al cargar la lista de bloqueados');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async () => {
    if (!unlockPhone) return;
    try {
      await axios.delete(`${API}/blacklist/${unlockPhone}`);
      toast.success(`Número ${unlockPhone} desbloqueado exitosamente.`);
      loadBlacklist();
    } catch (error) {
      console.error('Error unlocking phone:', error);
      toast.error('Error al desbloquear el número');
    }
    setUnlockPhone(null);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return format(new Date(dateString), "dd/MM/yyyy HH:mm", { locale: es });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground animate-pulse">Cargando Blacklist...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-background min-h-screen">
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <ShieldAlert className="h-8 w-8 text-destructive" />
              <div>
                <h1 className="text-3xl font-semibold tracking-tight">WhatsApp Blacklist</h1>
                <p className="text-muted-foreground mt-1">
                  Prevención de Spam (Meta Error 131049) - {blacklist.filter(b => b.activo).length} activos
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Card className="p-4 mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por número de teléfono..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </Card>

        {filteredList.length === 0 ? (
          <Card className="p-12 text-center">
            <ShieldAlert className="h-12 w-12 mx-auto mb-4 opacity-20" />
            <h3 className="text-lg font-medium text-foreground">Blacklist Vacía</h3>
            <p className="text-muted-foreground mt-2">
              No hay números penalizados temporalmente por WhatsApp.
            </p>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredList.map((item) => (
              <Card key={item.telefono} className={`p-5 flex flex-col justify-between ${!item.activo ? 'opacity-60' : 'border-destructive/50'}`}>
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 font-medium text-lg">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      {item.telefono}
                    </div>
                    {item.activo ? (
                      <Badge variant="destructive" className="animate-pulse">Bloqueado</Badge>
                    ) : (
                      <Badge variant="secondary">Inactivo</Badge>
                    )}
                  </div>
                  
                  <div className="space-y-2 text-sm text-muted-foreground mb-4">
                    <div className="flex items-center gap-2">
                      <History className="h-4 w-4" />
                      Reincidencias: <span className="font-semibold text-foreground">{item.penalizaciones}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Última Infracción: <span className="text-foreground">{formatDateTime(item.fechaPenalizacion)}</span>
                    </div>
                    {item.activo && item.expiresAt && (
                      <div className="flex items-center gap-2 text-destructive font-medium mt-2">
                        Expira: {formatDateTime(item.expiresAt)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-4 border-t mt-2">
                  <Button 
                    variant={item.activo ? "default" : "outline"}
                    className="w-full"
                    disabled={!item.activo}
                    onClick={() => setUnlockPhone(item.telefono)}
                  >
                    <Unlock className="h-4 w-4 mr-2" />
                    {item.activo ? "Remover Bloqueo" : "Desbloqueado"}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!unlockPhone} onOpenChange={() => setUnlockPhone(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Remover Número de la Blacklist?</AlertDialogTitle>
            <AlertDialogDescription>
              Estás a punto de desbloquear el número <strong>{unlockPhone}</strong>. 
              Esto reanudará el tráfico saliente de WhatsApp hacia este celular.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnlock}>Confirmar Desbloqueo</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Blacklist;
